import { AppBskyFeedLike, AppBskyFeedPost, AppBskyFeedRepost } from '@atproto/api'
import { db } from '../cache/db'

export type Category = 'posts' | 'replies' | 'likes' | 'reposts'
export const CATEGORIES: Category[] = ['posts', 'replies', 'likes', 'reposts']

export type Action = {
  category: Category
  rkey: string
  createdAt: string
  /** Subject ATP URI for likes/reposts, post URI for posts/replies */
  subjectOrUri?: string
}

export type Match = {
  precision: 'ms' | 'second'
  a: Action
  b: Action
}

export type CategoryStats = {
  category: Category
  countA: number
  countB: number
  /** Number of A actions that have ≥1 B action at the same second */
  aMatchedSecond: number
  /** Number of A actions that have ≥1 B action at the same ms */
  aMatchedMs: number
  bMatchedSecond: number
  bMatchedMs: number
  /** First 500 matches, sorted newest first */
  matches: Match[]
}

const COLLECTION_FOR: Record<Category, string> = {
  posts: 'app.bsky.feed.post',
  replies: 'app.bsky.feed.post',
  likes: 'app.bsky.feed.like',
  reposts: 'app.bsky.feed.repost',
}

async function gather(handle: string, category: Category): Promise<Action[]> {
  const collection = COLLECTION_FOR[category]
  const rows = await db.records
    .where('[handle+collection]')
    .equals([handle.toLowerCase(), collection])
    .toArray()
  const out: Action[] = []
  for (const r of rows) {
    const v = r.value
    let createdAt: string | undefined
    let subject: string | undefined
    if (AppBskyFeedPost.isRecord(v)) {
      const isReply = !!v.reply?.parent?.uri
      if (category === 'posts' && isReply) continue
      if (category === 'replies' && !isReply) continue
      createdAt = v.createdAt
      subject = v.reply?.parent?.uri ?? r.uri
    } else if (AppBskyFeedLike.isRecord(v)) {
      if (category !== 'likes') continue
      createdAt = v.createdAt
      subject = v.subject?.uri
    } else if (AppBskyFeedRepost.isRecord(v)) {
      if (category !== 'reposts') continue
      createdAt = v.createdAt
      subject = v.subject?.uri
    }
    if (!createdAt) continue
    const t = new Date(createdAt).getTime()
    if (Number.isNaN(t)) continue
    out.push({ category, rkey: r.rkey, createdAt, subjectOrUri: subject })
  }
  return out
}

export async function countCachedFor(handle: string, category: Category): Promise<number> {
  // Cheap: just count rows for the underlying collection. Distinguishing
  // posts vs replies requires walking the records, so we approximate by
  // returning the same collection count for both. Compare itself does the
  // real per-category split.
  const collection = COLLECTION_FOR[category]
  return await db.records
    .where('[handle+collection]')
    .equals([handle.toLowerCase(), collection])
    .count()
}

function compareCategory(category: Category, a: Action[], b: Action[]): CategoryStats {
  const bByMs = new Map<number, Action[]>()
  const bBySec = new Map<number, Action[]>()
  for (const x of b) {
    const t = new Date(x.createdAt).getTime()
    if (Number.isNaN(t)) continue
    const sec = Math.floor(t / 1000)
    let m = bByMs.get(t)
    if (!m) {
      m = []
      bByMs.set(t, m)
    }
    m.push(x)
    let s = bBySec.get(sec)
    if (!s) {
      s = []
      bBySec.set(sec, s)
    }
    s.push(x)
  }

  const aMatchedSec = new Set<string>()
  const aMatchedMs = new Set<string>()
  const bMatchedSec = new Set<string>()
  const bMatchedMs = new Set<string>()
  const matches: Match[] = []

  for (const x of a) {
    const t = new Date(x.createdAt).getTime()
    if (Number.isNaN(t)) continue
    const sec = Math.floor(t / 1000)

    const msList = bByMs.get(t)
    if (msList && msList.length > 0) {
      aMatchedMs.add(x.rkey)
      aMatchedSec.add(x.rkey)
      for (const m of msList) {
        matches.push({ precision: 'ms', a: x, b: m })
        bMatchedMs.add(m.rkey)
        bMatchedSec.add(m.rkey)
      }
      continue
    }
    const secList = bBySec.get(sec)
    if (secList && secList.length > 0) {
      aMatchedSec.add(x.rkey)
      for (const m of secList) {
        matches.push({ precision: 'second', a: x, b: m })
        bMatchedSec.add(m.rkey)
      }
    }
  }

  matches.sort((p, q) => new Date(q.a.createdAt).getTime() - new Date(p.a.createdAt).getTime())

  return {
    category,
    countA: a.length,
    countB: b.length,
    aMatchedSecond: aMatchedSec.size,
    aMatchedMs: aMatchedMs.size,
    bMatchedSecond: bMatchedSec.size,
    bMatchedMs: bMatchedMs.size,
    matches: matches.slice(0, 500),
  }
}

export async function compareAccounts(
  handleA: string,
  handleB: string,
): Promise<CategoryStats[]> {
  const out: CategoryStats[] = []
  for (const cat of CATEGORIES) {
    const [a, b] = await Promise.all([gather(handleA, cat), gather(handleB, cat)])
    out.push(compareCategory(cat, a, b))
  }
  return out
}

export type PairStats = CategoryStats & {
  iA: number
  iB: number
}

export type MultiCategoryStats = {
  category: Category
  /** Per-handle action counts, indexed by handle position */
  counts: number[]
  /** All pairwise comparisons (i < j) */
  pairs: PairStats[]
  /** Number of distinct seconds at which ALL handles had at least one action */
  allOverlapSec: number
  /** Number of distinct ms at which ALL handles had at least one action */
  allOverlapMs: number
}

export type MultiCompareResult = {
  handles: string[]
  categories: MultiCategoryStats[]
}

function timestampSets(actions: Action[]): { ms: Set<number>; sec: Set<number> } {
  const ms = new Set<number>()
  const sec = new Set<number>()
  for (const a of actions) {
    const t = new Date(a.createdAt).getTime()
    if (Number.isNaN(t)) continue
    ms.add(t)
    sec.add(Math.floor(t / 1000))
  }
  return { ms, sec }
}

function intersectAll<T>(sets: Set<T>[]): Set<T> {
  if (sets.length === 0) return new Set()
  const [first, ...rest] = sets
  if (rest.length === 0) return new Set(first)
  const out = new Set<T>()
  for (const v of first) {
    let inAll = true
    for (const s of rest) {
      if (!s.has(v)) {
        inAll = false
        break
      }
    }
    if (inAll) out.add(v)
  }
  return out
}

export async function compareMany(handles: string[]): Promise<MultiCompareResult> {
  const cleaned = handles.map((h) => h.trim().toLowerCase()).filter((h) => h.length > 0)
  const out: MultiCategoryStats[] = []
  for (const cat of CATEGORIES) {
    const actions = await Promise.all(cleaned.map((h) => gather(h, cat)))
    const counts = actions.map((a) => a.length)
    const pairs: PairStats[] = []
    for (let i = 0; i < cleaned.length; i++) {
      for (let j = i + 1; j < cleaned.length; j++) {
        pairs.push({ ...compareCategory(cat, actions[i], actions[j]), iA: i, iB: j })
      }
    }
    let allOverlapSec = 0
    let allOverlapMs = 0
    if (cleaned.length >= 2) {
      const sets = actions.map(timestampSets)
      allOverlapSec = intersectAll(sets.map((s) => s.sec)).size
      allOverlapMs = intersectAll(sets.map((s) => s.ms)).size
    }
    out.push({ category: cat, counts, pairs, allOverlapSec, allOverlapMs })
  }
  return { handles: cleaned, categories: out }
}

export type CacheStatus = {
  handle: string
  postCount: number
  likeCount: number
  repostCount: number
  hasAny: boolean
}

export async function cacheStatusFor(handle: string): Promise<CacheStatus> {
  const h = handle.toLowerCase()
  const [postCount, likeCount, repostCount] = await Promise.all([
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.post']).count(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.like']).count(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.repost']).count(),
  ])
  return {
    handle: h,
    postCount,
    likeCount,
    repostCount,
    hasAny: postCount + likeCount + repostCount > 0,
  }
}
